import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import { User, AuthContextType, Team } from "@/types";
import { supabase } from "@/index";
import { getGeneralUserData } from "@/api/general";

// Create a context for authentication
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

type AuthProviderProps = {
  children:
    | React.ReactNode
    | ((authContext: AuthContextType) => React.ReactNode);
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState<Team[] | undefined>(undefined);
  const [activeTeam, setActiveTeam] = useState<Team | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const fetchGeneralData = useCallback(async () => {
    try {
      setError(null);
      const data = await getGeneralUserData();

      setTeams(data.teams);

      // Set active team from localStorage or default to first team
      if (data.teams && data.teams.length > 0) {
        const savedTeamId = localStorage.getItem("activeTeamId");
        if (savedTeamId) {
          const savedTeam = data.teams.find((team) => team.id === savedTeamId);
          if (savedTeam) {
            setActiveTeam(savedTeam);
          } else {
            // If saved team not found, default to first team
            setActiveTeam(data.teams[0]);
          }
        } else if (!activeTeam) {
          // If no saved team and no active team, default to first team
          setActiveTeam(data.teams[0]);
        }
      }

      setIsLoading(false);
      setRetryCount(0); // Reset retry count on success
      return data;
    } catch (error: any) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";

      if (error.isAuthError) {
        setUser(null);
        setIsLoading(false);
        supabase.auth.signOut();
      } else {
        console.error("Error fetching user data:", errorMessage);
        setError(errorMessage);
        setRetryCount((prev) => prev + 1);
        throw error; // Re-throw the error so the caller can handle it if needed
      }
    }
  }, [activeTeam]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);

        // Fetch user's teams if logged in
        if (session?.user && event === "SIGNED_IN") {
          fetchGeneralData().catch((err) => {
            console.error("Failed to fetch general data on auth change:", err);
          });
        } else {
          setIsLoading(false);
          setError(null);
          setRetryCount(0);
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [fetchGeneralData]);

  // Retry fetch if we have errors and haven't exceeded max retries
  useEffect(() => {
    if (error && retryCount < maxRetries && user) {
      const retryTimer = setTimeout(() => {
        console.log(
          `Retrying general data fetch (attempt ${
            retryCount + 1
          }/${maxRetries})...`
        );
        fetchGeneralData().catch((err) => {
          console.error(`Retry attempt ${retryCount + 1} failed:`, err);
        });
      }, 2000 * Math.pow(2, retryCount)); // Exponential backoff: 2s, 4s, 8s...

      return () => clearTimeout(retryTimer);
    }
  }, [error, retryCount, user, fetchGeneralData]);

  const refetchGeneralData = async () => {
    setRetryCount(0); // Reset retry count
    setError(null);
    setIsLoading(true);
    try {
      return await fetchGeneralData();
    } catch (error) {
      console.error("Manual refetch of general data failed:", error);
      return null;
    }
  };

  // Function to switch active team
  const switchTeam = (teamId: string) => {
    if (!teams) return;

    const team = teams.find((t) => t.id === teamId);
    if (team) {
      setActiveTeam(team);
      // We don't need to update localStorage here since it's handled in the TeamSelector
    }
  };

  const contextValue = {
    user,
    setUser,
    isLoading,
    teams,
    setTeams,
    activeTeam,
    setActiveTeam,
    switchTeam,
    error,
    refetchGeneralData,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {typeof children === "function" ? children(contextValue) : children}
    </AuthContext.Provider>
  );
}
